import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button } from '../button'
import { Gate } from '../gate'
import { Spinner } from '../spinner'
import { Modal } from '../modal'
import { ConfirmDeleteDialog } from '../confirm-delete-dialog'
import { RoleFormModal } from './role-form-modal'
import { memberApi } from 'src/services/members/api'
import { memberQueries } from 'src/services/members/queries'
import { Member, Role } from 'src/services/members/models'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { InfoHint } from 'src/components/info-hint'

interface RolesSectionProps {
  orgId: string
}

export const RolesSection: React.FC<RolesSectionProps> = ({ orgId }) => {
  const { t } = useTranslation()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [roleInUse, setRoleInUse] = useState<{ role: Role; count: number } | null>(null)

  const { data: roles, isLoading: isLoadingRoles } = useQuery(memberQueries.roles())
  const { data: members } = useQuery(memberQueries.list(orgId))

  // Compute member count per role
  const memberCountByRole = useMemo(() => {
    const counts: Record<string, number> = {}
    if (members) {
      for (const member of members as Member[]) {
        counts[member.roleName] = (counts[member.roleName] || 0) + 1
      }
    }
    return counts
  }, [members])

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: (roleId: string) => memberApi.csc.deleteRole(roleId),
    queryKey: memberQueries.roles().queryKey,
  })

  // Roles still assigned to members can't be deleted — steer the admin to
  // reassign first instead of opening the delete confirmation.
  const handleDeleteClick = (role: Role) => {
    const count = memberCountByRole[role.name] || 0
    if (count > 0) {
      setRoleInUse({ role, count })
    } else {
      requestDelete(role.id, role.displayName)
    }
  }

  const handleEdit = (role: Role) => {
    setEditingRole(role)
    setIsFormOpen(true)
  }

  const handleCreate = () => {
    setEditingRole(null)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingRole(null)
  }

  return (
    <Gate permission="organization.manage_roles">
      <div
        className="rounded-lg border border-border bg-surface p-6 shadow-sm"
        data-testid="roles-section"
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-text-primary">
              {t('roles.title')}
              <InfoHint text={t('roles.subtitle')} className="ml-1.5" />
            </h3>
          </div>
          <Button
            variant="primary"
            onClick={handleCreate}
            icon={<PlusIcon className="h-4 w-4" />}
            data-testid="create-role-button"
          >
            {t('roles.createRole')}
          </Button>
        </div>

        {isLoadingRoles ? (
          <div className="flex items-center justify-center py-8" data-testid="roles-loading">
            <Spinner size="md" />
          </div>
        ) : !roles || (roles as Role[]).length === 0 ? (
          <div className="rounded-md border border-border bg-surface p-4 text-center text-sm text-text-placeholder">
            {t('roles.emptyState')}
          </div>
        ) : (
          <div
            className="overflow-x-auto rounded-md border border-border"
            data-testid="roles-table"
          >
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-surface-hover text-xs uppercase text-text-secondary">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('roles.roleName')}</th>
                  <th className="px-4 py-3 font-medium">{t('roles.members')}</th>
                  <th className="px-4 py-3 font-medium">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {(roles as Role[]).map((r) => {
                  const count = memberCountByRole[r.name] || 0
                  return (
                    <tr key={r.id} data-testid={`role-row-${r.id}`}>
                      <td className="whitespace-nowrap px-4 py-3 text-text-primary">
                        {r.displayName}
                        {r.isDefault && (
                          <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-text-placeholder">
                            {t('roles.defaultBadge')}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-text-secondary">{count}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            tooltip={t('roles.editRole')}
                            onClick={() => handleEdit(r)}
                            data-testid={`edit-role-${r.id}`}
                          >
                            <PencilIcon className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          {!r.isDefault && (
                            <Button
                              variant="danger"
                              size="sm"
                              iconOnly
                              tooltip={t('roles.deleteRole')}
                              onClick={() => handleDeleteClick(r)}
                              data-testid={`delete-role-${r.id}`}
                            >
                              <TrashIcon className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Role-in-use guard: shown instead of the delete confirmation when the
            role still has members assigned. */}
        <Modal
          isOpen={!!roleInUse}
          onClose={() => setRoleInUse(null)}
          title={t('roles.deleteRole')}
          size="sm"
        >
          <p className="text-sm text-text-secondary" data-testid="role-in-use-message">
            {t('roles.roleInUse', { count: roleInUse?.count ?? 0 })}
          </p>
          <div className="mt-6 flex justify-end">
            <Button variant="ghost" onClick={() => setRoleInUse(null)}>
              {t('common.close')}
            </Button>
          </div>
        </Modal>

        {/* Delete role confirmation */}
        <ConfirmDeleteDialog {...dialogProps} />

        <RoleFormModal isOpen={isFormOpen} onClose={handleCloseForm} role={editingRole} />
      </div>
    </Gate>
  )
}
